import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiClock, FiMapPin } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiUsers, number: '10,000+', label: 'Happy Customers' },
    { icon: FiClock, number: '15+', label: 'Years Experience' },
    { icon: FiAward, number: '50+', label: 'Brand Partners' },
    { icon: FiMapPin, number: '1', label: 'Trusted Location' }
  ];

  return (
    <section id="about" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About <span className="gradient-text">Mahanama Opticians</span>
            </h2>
            
            <div className="space-y-6 text-white/80 leading-relaxed">
              <p className="text-lg">
                Established in the heart of Embilipitiya's New Town, Mahanama Opticians has been serving 
                the community with exceptional eye care and premium eyewear for over 15 years.
              </p>
              
              <p>
                Located strategically at No 65/C New Town Road, we've witnessed Embilipitiya's transformation 
                from a small town to a thriving commercial hub. As the town evolved with major infrastructure 
                developments like the Mattala International Airport and Sooriyawewa International Cricket Stadium, 
                we've grown alongside our community.
              </p>
              
              <p>
                Our mission is to provide world-class eye care services and premium eyewear collections 
                that rival national chains, while maintaining the personal touch and trust that only a 
                local business can offer.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-12"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  'Comprehensive eye testing with modern equipment',
                  'Premium frame collections from global brands',
                  'Expert optometrists with decades of experience',
                  'Convenient location in New Town with ample parking',
                  'Personalized service for every customer',
                  'Competitive pricing with quality guarantee'
                ].map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span className="text-white/70">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="glass-card p-8 text-center"
              >
                <div className="glass-icon mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="text-3xl text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-white/70">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-24 text-center"
        >
          <div className="glass-card p-12 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">Our Vision</h3>
            <p className="text-xl text-white/80 leading-relaxed">
              "To be Embilipitiya's premier destination for comprehensive eye care, combining 
              cutting-edge technology with personalized service to help our community see 
              the world more clearly, one pair of glasses at a time."
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;